import { X, Phone, CheckCircle, XCircle, Clock, Instagram, Youtube, Facebook } from 'lucide-react'
import { useState } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { trackCallbackRequest } from '../utils/analytics'

interface CallbackModalProps {
  isOpen: boolean
  onClose: () => void
}

const CallbackModal = ({ isOpen, onClose }: CallbackModalProps) => {
  const { t, language } = useLanguage()
  const whatsappNumber = '905536576454' // Canlı destek numarası
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    ecommerce: '',
    source: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Form verilerini formatla
    const ecommerceText = formData.ecommerce === 'yes' 
      ? (language === 'tr' ? 'Evet' : 'Yes')
      : formData.ecommerce === 'no'
      ? (language === 'tr' ? 'Hayır' : 'No')
      : formData.ecommerce === 'starting'
      ? (language === 'tr' ? 'Yeni Başlıyorum' : 'Just Starting')
      : ''
    
    const sourceText = formData.source === 'instagram' ? 'Instagram'
      : formData.source === 'youtube' ? 'Youtube'
      : formData.source === 'facebook' ? 'Facebook'
      : formData.source === 'x' ? 'X (Twitter)'
      : formData.source === 'customer' 
      ? (language === 'tr' ? 'Thinklyze Müşterilerinden' : 'From Thinklyze Customers')
      : ''
    
    // WhatsApp mesajı formatı
    const message = language === 'tr'
      ? `*Sizi Arayalım Formu*\n\n` +
        `*Ad Soyad:* ${formData.name}\n` +
        `*Email:* ${formData.email}\n` +
        `*Telefon:* ${formData.phone || 'Belirtilmemiş'}\n` +
        `*Şirket:* ${formData.company || 'Belirtilmemiş'}\n` +
        `*E-ticaret Yapıyor musunuz?* ${ecommerceText}\n` +
        `*Thinklyze'ı Nereden Duydunuz?* ${sourceText || 'Belirtilmemiş'}`
      : `*Call Me Back Form*\n\n` +
        `*Name:* ${formData.name}\n` +
        `*Email:* ${formData.email}\n` +
        `*Phone:* ${formData.phone || 'Not specified'}\n` +
        `*Company:* ${formData.company || 'Not specified'}\n` +
        `*Do you do e-commerce?* ${ecommerceText}\n` +
        `*Where did you hear about Thinklyze?* ${sourceText || 'Not specified'}`
    
    // Track callback request
    trackCallbackRequest()
    
    // WhatsApp Web'e yönlendir
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`
    window.open(whatsappUrl, '_blank')
    
    // Formu temizle ve modal'ı kapat
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      ecommerce: '',
      source: ''
    })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[600px] w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Phone className="w-5 h-5 text-gray-900" />
            <h2 className="text-lg md:text-xl font-bold text-gray-900">{t('callback.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 md:p-5 space-y-4">
          {/* Name & Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">
                {t('callback.name')}
              </label>
              <input
                type="text"
                placeholder={t('callback.namePlaceholder')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">
                {t('callback.email')}
              </label>
              <input
                type="email"
                placeholder={t('callback.emailPlaceholder')}
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                required
              />
            </div>
          </div>

          {/* Phone & Company */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">
                {t('callback.phone')}
              </label>
              <input
                type="tel"
                placeholder={t('callback.phonePlaceholder')}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-900 mb-1.5">
                {t('callback.company')}
              </label>
              <input
                type="text"
                placeholder={t('callback.companyPlaceholder')}
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* E-commerce Status */}
          <div>
            <label className="block text-xs font-medium text-gray-900 mb-2">
              {t('callback.ecommerce')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, ecommerce: 'yes' })}
                className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                  formData.ecommerce === 'yes'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <span className="text-xs font-medium text-gray-900">{t('callback.yes')}</span>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, ecommerce: 'no' })}
                className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                  formData.ecommerce === 'no'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-4 h-4 text-red-600" />
                </div>
                <span className="text-xs font-medium text-gray-900">{t('callback.no')}</span>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, ecommerce: 'starting' })}
                className={`flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg border-2 transition-all ${
                  formData.ecommerce === 'starting'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <span className="text-xs font-medium text-gray-900 leading-tight text-center">{t('callback.starting')}</span>
              </button>
            </div>
          </div>

          {/* Source */}
          <div>
            <label className="block text-xs font-medium text-gray-900 mb-2">
              {t('callback.source')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, source: 'instagram' })}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all ${
                  formData.source === 'instagram'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Instagram className="w-4 h-4 text-gray-900" />
                <span className="text-xs font-medium text-gray-900">Instagram</span>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, source: 'youtube' })}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all ${
                  formData.source === 'youtube'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Youtube className="w-4 h-4 text-gray-900" />
                <span className="text-xs font-medium text-gray-900">Youtube</span>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, source: 'facebook' })}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all ${
                  formData.source === 'facebook'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Facebook className="w-4 h-4 text-gray-900" />
                <span className="text-xs font-medium text-gray-900">Facebook</span>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, source: 'x' })}
                className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all ${
                  formData.source === 'x'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <svg className="w-3.5 h-3.5 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                <span className="text-xs font-medium text-gray-900">X</span>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, source: 'customer' })}
                className={`col-span-2 flex items-center justify-center gap-2 p-2.5 rounded-lg border-2 transition-all ${
                  formData.source === 'customer'
                    ? 'border-gray-900 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-xs font-medium text-gray-900">{t('callback.customer')}</span>
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
          >
            <Phone className="w-4 h-4" />
            {t('callback.callMe')}
          </button>
        </form>
      </div>
    </div>
  )
}

export default CallbackModal















